import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  Avatar,
  Chip,
  Divider,
  CircularProgress,
  Button,
  Alert,
} from "@mui/material";
import * as authService from "../../api/auth";
import useAuth from "../../hooks/useAuth";
import { getInitials } from "../../utils/userDisplay";

export default function MyProfile() {
  const { refreshProfile } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [verifyMsg, setVerifyMsg] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const me = await authService.getMe();
      setData(me);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Failed to load profile");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onResendVerification = async () => {
    setVerifyMsg(null);
    try {
      await authService.resendVerification();
      setVerifyMsg("If your account is unverified, a new email was sent.");
      await refreshProfile();
    } catch (e) {
      setVerifyMsg(
        e?.response?.data?.detail || e?.message || "Could not send email."
      );
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
        <Button sx={{ mt: 2 }} variant="outlined" onClick={load}>
          Retry
        </Button>
      </Box>
    );
  }

  if (!data) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>No profile data.</Typography>
      </Box>
    );
  }

  const displayName =
    [data.firstname, data.lastname].filter(Boolean).join(" ").trim() || data.username;
  const initials = getInitials({ profile: data, email: data.email, username: data.username });

  const verified = Boolean(data.email_verified_at);

  return (
    <Box sx={{ p: { xs: 0, sm: 1 }, maxWidth: 720, mx: "auto" }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
        My profile
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        This is how you appear to your organization. Contact an administrator
        if you need your name or roles changed.
      </Typography>

      {!verified && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={onResendVerification}>
              Resend link
            </Button>
          }
        >
          Your email is not verified yet. Verify to unlock all actions (e.g.
          sending invitations).
        </Alert>
      )}
      {verifyMsg && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setVerifyMsg(null)}>
          {verifyMsg}
        </Alert>
      )}

      <Paper
        elevation={3}
        sx={{
          p: 3,
          borderRadius: 2,
          border: (t) => `1px solid ${t.palette.divider}`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
          <Avatar
            sx={{
              width: 88,
              height: 88,
              fontSize: 32,
              background:
                "linear-gradient(135deg, rgba(42,152,255,0.9), rgba(2,130,172,0.9))",
            }}
          >
            {initials}
          </Avatar>
          <Box>
            <Typography variant="h5" fontWeight={700}>
              {displayName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {data.tenant_name || "No organization"}
            </Typography>
            <Chip
              label={verified ? "Email verified" : "Email not verified"}
              color={verified ? "success" : "warning"}
              size="small"
              sx={{ mt: 1 }}
            />
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <List disablePadding>
          <ListItem sx={{ px: 0, alignItems: "flex-start" }}>
            <ListItemText primary="Username" secondary={data.username} />
          </ListItem>
          <Divider component="li" sx={{ my: 1 }} />
          <ListItem sx={{ px: 0, alignItems: "flex-start" }}>
            <ListItemText primary="Email" secondary={data.email} />
          </ListItem>
          <Divider component="li" sx={{ my: 1 }} />
          <ListItem sx={{ px: 0, alignItems: "flex-start" }}>
            <ListItemText
              primary="Organization"
              secondary={data.tenant_name || "—"}
            />
          </ListItem>
          <Divider component="li" sx={{ my: 1 }} />
          <ListItem sx={{ px: 0, alignItems: "flex-start" }}>
            <ListItemText
              primary="Roles"
              secondary={
                data.roles?.length ? (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 0.5 }}>
                    {data.roles.map((role) => (
                      <Chip
                        key={role.id}
                        label={role.name}
                        color="primary"
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                ) : (
                  "No roles assigned"
                )
              }
            />
          </ListItem>
        </List>
      </Paper>
    </Box>
  );
}
